// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { pharmacyAppOrigin } from '../../auth/pharmacyUrl'
import { useNavigate } from 'react-router-dom'
import { Building2, Plus, Loader2, AlertCircle, ShieldAlert, CheckCircle2, Clock, KeyRound, LogIn, Trash2, ArchiveRestore } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { usePharmacy } from '../../auth/PharmacyContext'

/**
 * /admin/pharmacies — platform admin's view of every onboarded pharmacy.
 *
 * Sorts by onboarding state so what needs attention rises to the top:
 *   pending_baa → pending_admin → pending_ingest_setup → active → suspended.
 *
 * "Add pharmacy" launches the onboarding wizard at /admin/pharmacies/new.
 *
 * Click any row → detail page at /admin/pharmacies/{id} where the admin can
 * invite the primary admin, generate PioneerRx ingest credentials, suspend.
 */
export default function PharmaciesTab() {
  const navigate = useNavigate()
  const { enterPharmacy } = usePharmacy()
  const [items, setItems] = useState<api.PharmacyListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)

  async function reload() {
    setLoading(true); setError(null)
    try {
      const r = await api.listPharmacies({ includeArchived: showArchived })
      setItems(r.items)
    } catch (e) { setError(friendlyError(e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { reload() }, [showArchived])

  async function handleEnter(p: api.PharmacyListItem) {
    // WI-894: self-assign as a manager so the SaaS admin has explicit
    // membership inside the pharmacy. Best-effort — the Enter still
    // Open the pharmacy in its OWN URL in a NEW TAB. The /p/:slug route sets that tab's active
    // pharmacy from the URL (per-tab sessionStorage), so the admin's other tabs are unaffected and
    // the pharmacy's context can't bleed across logins. Self-assign first so the view has access.
    try {
      await api.selfAssignPharmacyManager(p.id)
    } catch {
      // Fall through; not blocking
    }
    const slug = p.slug ?? ''
    const pathId = p.url_code ?? slug  // prefer opaque code; slug still resolves as fallback
    if (pathId) {
      window.open(`${pharmacyAppOrigin()}/p/${pathId}`, '_blank', 'noopener')
    } else {
      // No slug (shouldn't happen post-onboarding) — fall back to same-tab dial-in.
      enterPharmacy({ id: p.id, name: p.name, slug: '', logo_url: p.logo_url ?? null, primary_color_hex: p.primary_color_hex ?? null })
      navigate('/')
    }
  }

  async function handleArchive(p: api.PharmacyListItem) {
    const confirmed = window.confirm(
      `Archive pharmacy "${p.name}"?\n\n` +
      `It will be hidden from default lists but data is preserved.\n` +
      `You can unarchive later from the "Show archived" filter.`
    )
    if (!confirmed) return
    try {
      await api.archivePharmacy(p.id, 'Archived from SaaS pharmacies list')
      await reload()
    } catch (e) {
      alert('Archive failed: ' + friendlyError(e))
    }
  }

  async function handleUnarchive(p: api.PharmacyListItem) {
    try {
      await api.unarchivePharmacy(p.id)
      await reload()
    } catch (e) {
      alert('Unarchive failed: ' + friendlyError(e))
    }
  }

  async function handleHardDelete(p: api.PharmacyListItem) {
    const slug = p.slug ?? ''
    const confirmed = window.prompt(
      `HARD DELETE pharmacy "${p.name}"?\n\n` +
      `This cascades all child data and CANNOT be undone.\n` +
      `To confirm, type the slug: ${slug}`
    )
    if (confirmed !== slug) {
      if (confirmed !== null) alert('Slug did not match. Delete cancelled.')
      return
    }
    try {
      const r = await api.hardDeletePharmacy(p.id, slug)
      alert(`Deleted "${p.name}". ${r.child_rows_total ?? 0} child rows cascaded.`)
      await reload()
    } catch (e) {
      alert('Hard delete failed: ' + friendlyError(e))
    }
  }

  return (
    <div>
      <div style={headerRowStyle}>
        <div>
          <h2 style={h2Style}><Building2 size={16} aria-hidden="true" /> Pharmacies</h2>
          <p style={subtitleStyle}>
            Every pharmacy onboarded to OwlRx Portal. Click "Enter" on a pharmacy
            to manage it as a SaaS admin; click the row name to see onboarding
            details.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-mute)' }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={e => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          <button onClick={() => navigate('/admin/pharmacies/new')} style={primaryBtnStyle}>
            <Plus size={14} /> Onboard pharmacy
          </button>
        </div>
      </div>

      {error && <div style={errorBox}><AlertCircle size={14} /> {error}</div>}

      {loading ? (
        <div style={loadingBox}><Loader2 size={16} className="spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div style={emptyBox}>
          <Building2 size={28} style={{ color: 'var(--color-text-mute)' }} />
          <h3 style={{ fontSize: 16, margin: '8px 0 4px' }}>No pharmacies onboarded</h3>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-soft)' }}>
            Click "Onboard pharmacy" to start. You'll need a BAA reference and the name + email of the COO who approved.
          </p>
        </div>
      ) : (
        <ul style={listStyle}>
          {items.map(p => (
            <PharmacyRow
              key={p.id}
              item={p}
              onClickRow={() => navigate(`/admin/pharmacies/${p.id}`)}
              onEnter={() => handleEnter(p)}
              onArchive={() => handleArchive(p)}
              onUnarchive={() => handleUnarchive(p)}
              onHardDelete={() => handleHardDelete(p)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

interface RowProps {
  item: api.PharmacyListItem
  onClickRow: () => void
  onEnter: () => void
  onArchive: () => void
  onUnarchive: () => void
  onHardDelete: () => void
}

function PharmacyRow({ item, onClickRow, onEnter, onArchive, onUnarchive, onHardDelete }: RowProps) {
  const isArchived = Boolean(item.archived_at)
  return (
    <li style={{ ...rowStyle, opacity: isArchived ? 0.6 : 1 }}>
      <div
        style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1, cursor: 'pointer' }}
        onClick={onClickRow}
      >
        <StateBadge state={item.onboarding_state} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={nameRowStyle}>
            <strong style={nameStyle}>{item.name}</strong>
            {isArchived && <span style={pillArchived}>Archived</span>}
            {item.npi && <span style={pillCat}>NPI {item.npi}</span>}
            {item.ncpdp && <span style={pillCat}>NCPDP {item.ncpdp}</span>}
            {item.sso_provider !== 'none' && <span style={pillSso}>SSO: {item.sso_provider}</span>}
            {item.parent_organization_name && (
              <span style={pillOrg}>
                {item.parent_organization_name}{item.branch_label ? ` · ${item.branch_label}` : ''}
              </span>
            )}
          </div>
          <div style={metaLineStyle}>
            {[item.city, item.state_code].filter(Boolean).join(', ') || <span style={mutedSmall}>No address</span>}
            {' · '}
            {item.active_users} active user{item.active_users === 1 ? '' : 's'}
            {item.pending_invitations > 0 && (
              <span style={{ color: '#a16207', marginLeft: 6 }}>· {item.pending_invitations} pending invite{item.pending_invitations === 1 ? '' : 's'}</span>
            )}
            {(() => {
              const used = item.storage_used_bytes ?? 0
              const quota = item.storage_quota_bytes ?? 0
              if (!quota) return null
              const gb = (b: number) => b / 1073741824
              const pct = Math.min(100, Math.round((used / quota) * 100))
              const color = pct >= 95 ? '#dc2626' : pct >= 80 ? '#d97706' : 'var(--color-text-mute)'
              return <span style={{ color, marginLeft: 6 }}>· {gb(used).toFixed(2)}/{gb(quota).toFixed(0)} GB ({pct}%)</span>
            })()}
          </div>
          <div style={metaLineStyle}>
            {item.baa_reference ? (
              <span>BAA: <code style={codeStyle}>{item.baa_reference}</code></span>
            ) : <span style={mutedSmall}>No BAA recorded</span>}
            {item.active_creds_created_at && (
              <>
                {' · '}
                <span style={{ color: '#166534' }}>
                  <KeyRound size={11} style={{ display: 'inline', verticalAlign: '-1px' }} /> Ingest credentials active
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
        {!isArchived && (
          <button onClick={onEnter} style={enterBtnStyle} title="Enter this pharmacy view">
            <LogIn size={12} aria-hidden="true" /> Enter
          </button>
        )}
        {!isArchived ? (
          <button onClick={onArchive} style={subtleBtnStyle} title="Archive (hide from default list, reversible)">
            Archive
          </button>
        ) : (
          <button onClick={onUnarchive} style={subtleBtnStyle} title="Unarchive">
            <ArchiveRestore size={12} aria-hidden="true" /> Unarchive
          </button>
        )}
        <button onClick={onHardDelete} style={dangerBtnStyle} title="Hard delete (cascade, irreversible)">
          <Trash2 size={12} aria-hidden="true" /> Delete
        </button>
      </div>
    </li>
  )
}

function StateBadge({ state }: { state: api.PharmacyListItem['onboarding_state'] }) {
  const config = {
    pending_baa:           { color: '#a16207', bg: '#fef3c7', icon: Clock,        label: 'Pending BAA' },
    pending_provision:     { color: '#6d28d9', bg: '#ede9fe', icon: Clock,        label: 'Provisioning' },
    pending_admin:         { color: '#1e40af', bg: '#dbeafe', icon: Clock,        label: 'Pending admin' },
    pending_ingest_setup:  { color: '#0891b2', bg: '#cffafe', icon: KeyRound,     label: 'Needs creds' },
    active:                { color: '#166534', bg: '#dcfce7', icon: CheckCircle2, label: 'Active' },
    suspended:             { color: '#991b1b', bg: '#fee2e2', icon: ShieldAlert,  label: 'Suspended' },
  }[state] ?? { color: '#475569', bg: '#f1f5f9', icon: Clock, label: state }
  const Icon = config.icon
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 8px', borderRadius: 4, minWidth: 110,
      background: config.bg, color: config.color,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
      whiteSpace: 'nowrap',
    }}>
      <Icon size={12} aria-hidden="true" />
      {config.label}
    </div>
  )
}

const headerRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  gap: 16, marginBottom: 16, flexWrap: 'wrap',
}
const h2Style: React.CSSProperties = { fontSize: 18, fontWeight: 600, margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }
const subtitleStyle: React.CSSProperties = { margin: '4px 0 0', color: 'var(--color-text-soft)', fontSize: 13, maxWidth: 600 }
const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', fontSize: 13, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff', border: 'none',
  borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
}
const listStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 }
const rowStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: 14, marginBottom: 8,
  display: 'flex', alignItems: 'flex-start', gap: 16,
  transition: 'border-color 0.1s, transform 0.05s',
}
const nameRowStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 6 }
const nameStyle: React.CSSProperties = { fontSize: 15 }
const metaLineStyle: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-soft)', marginTop: 2 }
const mutedSmall: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-mute)' }
const codeStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 11,
  background: 'var(--color-surface-alt)', padding: '1px 5px', borderRadius: 3,
}
const pillCat: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
  padding: '2px 8px', borderRadius: 10, background: '#f1f5f9', color: '#475569',
}
const pillSso: React.CSSProperties = { ...pillCat, background: '#ede9fe', color: '#6d28d9' }
const pillOrg: React.CSSProperties = { ...pillCat, background: '#dbeafe', color: '#1e40af' }
const pillArchived: React.CSSProperties = { ...pillCat, background: '#f3f4f6', color: '#6b7280' }

// WI-893 row action buttons
const enterBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', fontSize: 12, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff',
  border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
  whiteSpace: 'nowrap',
}
const subtleBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 9px', fontSize: 11,
  background: 'transparent', color: 'var(--color-text-soft)',
  border: '1px solid var(--color-border)', borderRadius: 4,
  cursor: 'pointer', fontFamily: 'inherit',
}
const dangerBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 9px', fontSize: 11,
  background: 'transparent', color: '#991b1b',
  border: '1px solid #fecaca', borderRadius: 4,
  cursor: 'pointer', fontFamily: 'inherit',
}
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginBottom: 8,
}
const loadingBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: 24, color: 'var(--color-text-soft)', fontSize: 14 }
const emptyBox: React.CSSProperties = {
  padding: 32, textAlign: 'center' as const,
  background: 'var(--color-surface, #fff)', border: '1px dashed var(--color-border)', borderRadius: 8,
}
